import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { deliverExport, EventCreator, WeekPreview } from '@project/event-creator-web';
import { icsTimestamp, type EventDraft } from '@project/event-format';
import { exportCalendar, type EventCreatorState } from '@project/event-creator';
import {
  appendIntakeBatch,
  buildIntakeBatch,
  classifyIntakeMedia,
  removeIntakeForm,
  updateIntakeForm,
  type IntakeBatch,
} from '@project/core';

import { UnsavedDialog } from './UnsavedDialog';
import { EventList } from './EventList';
import { Funnel } from './Funnel';
import { readRecovery, writeRecovery } from './recovery';
import { fillSite, rememberLanguage, SITE_LANGUAGES, type Language } from './i18n';
import { localizePage, pageTranslationKey, PAGES, type Page } from './pages';

/**
 * What the prerendered HTML holds where the creator will go. It is real copy rather than a grey
 * box: a crawler reads it, someone on a slow connection reads it, and someone with JavaScript
 * off gets an explanation instead of an empty card. `min-height` keeps the page from jumping
 * when the creator replaces it.
 */
function CreatorPlaceholder({ m }: { readonly m: Language['site'] }): React.JSX.Element {
  return (
    <div className="creator-placeholder">
      <p>{m.creatorPlaceholder}</p>
      <noscript>
        <p className="muted">{m.creatorNoScript}</p>
      </noscript>
    </div>
  );
}

/** The path a page serves from — the shape used by its links, its file, and the sitemap. */
export function pathFor(page: Page, language = 'en'): string {
  const pagePath = page.slug === '' ? '' : `${page.slug}/`;
  return language === 'en' ? `/${pagePath}` : `/${language}/${pagePath}`;
}

/** The two ways in. The form is first because it is what most people came for. */
type Tab = 'form' | 'funnel';

export function App({
  page,
  language,
  strings,
}: {
  readonly page: Page;
  readonly language: string;
  readonly strings: Language;
}): React.JSX.Element {
  const [previewDraft, setPreviewDraft] = useState<EventDraft | null>(null);
  const [tab, setTab] = useState<Tab>('form');
  const [text, setText] = useState('');
  const [editorSession, setEditorSession] = useState<EventCreatorState | null>(null);
  const restoredSession = useRef<EventCreatorState | undefined>(undefined);
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
  const hrefFor = (target: Page, targetLanguage = language): string =>
    `${basePath}${pathFor(target, targetLanguage)}`;
  // Everything gathered so far, from either tab. `null` until the first event lands, so the page
  // opens as the single-event tool it has always been rather than as an empty list.
  const [collected, setCollected] = useState<IntakeBatch | null>(null);
  // Which collected event the form is editing, if any. When it is null the form is building
  // something new, and its button says so.
  const [editing, setEditing] = useState<string | null>(null);
  // A draft handed to the builder to be corrected. The key remounts it, so a second one replaces
  // the first rather than being ignored.
  const [loaded, setLoaded] = useState<EventDraft | undefined>(undefined);
  const [loadedKey, setLoadedKey] = useState(0);
  const [runs, setRuns] = useState(0);
  const [reviewKey, setReviewKey] = useState(0);
  const reviewTarget = useRef<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const savedDraft = collected?.forms.find((form) => form.id === editing)?.draft;
  const hasUnsavedChanges = Boolean(
    savedDraft &&
    previewDraft &&
    JSON.stringify({ ...previewDraft, uid: undefined }) !==
      JSON.stringify({ ...savedDraft, uid: undefined }),
  );

  const initialDraft = useRef<EventDraft | null>(null);
  const observeDraft = useCallback((draft: EventDraft): void => {
    initialDraft.current ??= draft;
    setPreviewDraft(draft);
  }, []);
  const [pendingEditor, setPendingEditor] = useState<{
    draft: EventDraft | undefined;
    formId: string | null;
    returnTo?: string;
  } | null>(null);
  const dirty =
    editing !== null
      ? hasUnsavedChanges
      : Boolean(
          previewDraft &&
          initialDraft.current &&
          JSON.stringify({ ...previewDraft, uid: undefined }) !==
            JSON.stringify({ ...initialDraft.current, uid: undefined }),
        );

  // Focus after React has rendered the new rows, including on a narrow screen where the
  // calendar is below the editor. Keyboard and screen-reader users arrive at the same result.
  useEffect(() => {
    if (reviewKey === 0) return;
    const calendar = document.getElementById(reviewTarget.current ?? 'calendar-events');
    calendar?.focus({ preventScroll: true });
    calendar?.scrollIntoView({ block: 'start' });
  }, [reviewKey]);

  /** Put a draft in the builder and take the user to it. */
  const replaceEditor = (draft: EventDraft | undefined, formId: string | null): void => {
    setRestored(false);
    setConfirmation('');
    reviewTarget.current = null;
    setPreviewDraft(draft ?? null);
    initialDraft.current = null;
    restoredSession.current = undefined;
    setEditorSession(null);
    setLoaded(draft);
    setLoadedKey((key) => key + 1);
    setEditing(formId);
    setTab('form');
  };

  const openInForm = (draft: EventDraft | undefined, formId: string | null): void => {
    if (formId !== null && formId === editing) {
      setTab('form');
      document.getElementById('ec-title')?.focus();
      return;
    }
    if (dirty) setPendingEditor({ draft, formId });
    else replaceEditor(draft, formId);
  };

  const collect = (incoming: IntakeBatch): IntakeBatch => {
    incoming = {
      ...incoming,
      forms: incoming.forms.map((form) => ({
        ...form,
        draft: { ...form.draft, uid: `${crypto.randomUUID()}@icsmaker.com` },
      })),
    };
    const append = (current: IntakeBatch | null): IntakeBatch =>
      appendIntakeBatch(
        current ?? buildIntakeBatch({ media: incoming.media, extractions: [] }),
        incoming,
      );
    setCollected(append);
    return append(collected);
  };

  const finishEditing = (formId: string): void => {
    replaceEditor(undefined, null);
    reviewTarget.current = `event-${formId}`;
    setReviewKey((key) => key + 1);
  };

  const cancelEditing = (): void => {
    if (editing === null) return;
    if (dirty) setPendingEditor({ draft: undefined, formId: null, returnTo: editing });
    else finishEditing(editing);
  };

  /** Saving finishes the edit; adding a new event also returns to its saved row. */
  const addFromForm = (draft: EventDraft): void => {
    if (editing !== null && collected !== null) {
      const uid = collected.forms.find((form) => form.id === editing)?.draft.uid;
      setCollected(updateIntakeForm(collected, editing, { ...draft, ...(uid ? { uid } : {}) }));
      finishEditing(editing);
      setConfirmation(
        fillSite(editMessages.updated, { title: draft.title || strings.site.listUntitled }),
      );
      return;
    }
    setRuns((count) => count + 1);
    const next = collect(
      buildIntakeBatch({
        id: `form-run-${runs + 1}`,
        media: classifyIntakeMedia({ text: draft.title || 'event' }),
        extractions: [{ draft, confidence: 1 }],
      }),
    );
    const formId = next.forms[next.forms.length - 1]?.id;
    if (formId) finishEditing(formId);
  };

  const remove = (formId: string): void => {
    if (collected === null) return;
    setCollected(removeIntakeForm(collected, formId));
    // The form was editing the row that just went. It is now building something new, and its
    // button has to say that rather than offering to save changes to nothing.
    if (formId === editing) setEditing(null);
  };

  const download = (drafts: readonly EventDraft[], fileName: string): void => {
    if (drafts.length === 0) return;
    const result = exportCalendar(drafts, {
      uid: `${crypto.randomUUID()}@icsmaker.com`,
      dtstamp: icsTimestamp(new Date()),
    });
    // Same direct file delivery the builder's own button uses, so iOS can hand the `.ics` to its
    // native Calendar importer whether it contains one event or a gathered batch.
    if (result.ok) deliverExport({ ...result.export, fileName });
  };
  // The creator is client-only on purpose. Its opening state is today's date and a fresh UID —
  // things a build cannot know — so prerendering it would bake the build day into the page and
  // hand React a hydration mismatch on the first paint. Rendering the placeholder on the server
  // *and* on the first client render keeps the two trees identical; the effect then swaps in the
  // real thing, one frame later, with the bundle already parsed.
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    const recovered = readRecovery();
    if (recovered) {
      restoredSession.current = recovered.session ?? undefined;
      initialDraft.current = recovered.baseline;
      setEditorSession(recovered.session);
      setPreviewDraft(recovered.session?.draft ?? null);
      setCollected(recovered.collected);
      setEditing(recovered.editing);
      setRuns(recovered.runs);
      setTab(recovered.editing ? 'form' : recovered.tab);
      setRestored(
        Boolean(
          recovered.session &&
          (recovered.editing !== null ||
            JSON.stringify(recovered.session.draft) !== JSON.stringify(recovered.baseline)),
        ),
      );
      setText(recovered.text);
    }
    setInteractive(true);
  }, []);

  useLayoutEffect(() => {
    if (!interactive) return;
    writeRecovery({
      version: 1,
      session: editorSession,
      baseline: initialDraft.current,
      collected,
      editing,
      runs,
      tab,
      text,
    });
  }, [interactive, editorSession, collected, editing, runs, tab, text]);

  useEffect(() => {
    if (loadedKey > 0 && reviewTarget.current === null) {
      document.getElementById('ec-title')?.focus({ preventScroll: true });
      document.getElementById('panel-form')?.scrollIntoView({ block: 'start' });
    }
  }, [loadedKey]);

  const chooseLanguage = (tag: string): void => {
    rememberLanguage(tag);
    window.location.assign(hrefFor(page, tag));
  };

  const m = strings.site;
  const editMessages = m.inlineEdit;
  const navigationPages = PAGES.map((candidate) =>
    localizePage(candidate, strings.pages?.[pageTranslationKey(candidate)]),
  );

  return (
    <div className="site">
      {pendingEditor && (
        <UnsavedDialog
          m={m.unsavedDialog}
          onKeep={() => setPendingEditor(null)}
          onDiscard={() => {
            if (pendingEditor.returnTo) finishEditing(pendingEditor.returnTo);
            else replaceEditor(pendingEditor.draft, pendingEditor.formId);
            setPendingEditor(null);
          }}
          onSave={() => {
            if (previewDraft) addFromForm(previewDraft);
            if (!pendingEditor.returnTo) replaceEditor(pendingEditor.draft, pendingEditor.formId);
            setPendingEditor(null);
          }}
        />
      )}
      <header className="site-header">
        <div className="site-header-top">
          {page.slug !== '' ? (
            <p className="crumb">
              <a href={hrefFor(navigationPages[0]!)}>ICS Maker</a>
              <span aria-hidden="true"> / </span>
              <span aria-current="page">{page.navLabel}</span>
            </p>
          ) : (
            <span aria-hidden="true" />
          )}
          <div className="language-pick">
            <label htmlFor="site-language">{m.languageLabel}</label>
            <select
              id="site-language"
              value={language}
              onChange={(event) => chooseLanguage(event.target.value)}
            >
              {SITE_LANGUAGES.map(([tag, name]) => (
                <option key={tag} value={tag}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="site-heading">
          <h1>{page.h1}</h1>
          <p className="lede">{page.lede}</p>
        </div>
      </header>

      <main>
        <aside className="getting-started" aria-labelledby="getting-started-heading">
          <h2 id="getting-started-heading">{m.quickStart.heading}</h2>
          <ol>
            {m.quickStart.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </aside>
        <section className="maker-workspace">
          {interactive ? (
            <>
              <div className="card maker-editor">
                {Boolean(collected?.forms.length) && (
                  <a className="workspace-link" href="#calendar-events">
                    {fillSite(m.workflow.headline, { count: collected?.forms.length ?? 0 })}
                  </a>
                )}
                {/* Two ways in, one at a time. The form is the default because it is what most
                  people came for; the other tab is where a season, a document or a photographed
                  flyer goes. Both feed the same list underneath, and nothing is uploaded by
                  either — there is no key to configure and no request to make. */}
                <div
                  hidden={editing !== null}
                  className="tabs"
                  role="tablist"
                  aria-label={m.tabsLabel}
                  onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                    event.preventDefault();
                    const next =
                      event.key === 'Home'
                        ? 'form'
                        : event.key === 'End'
                          ? 'funnel'
                          : tab === 'form'
                            ? 'funnel'
                            : 'form';
                    setTab(next);
                    document.getElementById(`tab-${next}`)?.focus();
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    id="tab-form"
                    aria-selected={tab === 'form'}
                    tabIndex={tab === 'form' ? 0 : -1}
                    aria-controls="panel-form"
                    aria-describedby={m.entryHints ? 'form-hint' : undefined}
                    className={`tab${tab === 'form' ? ' tab-current' : ''}`}
                    onClick={() => setTab('form')}
                  >
                    <span>{m.tabForm}</span>
                    {m.entryHints && (
                      <small id="form-hint" aria-hidden="true">
                        {m.entryHints.form}
                      </small>
                    )}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="tab-funnel"
                    aria-selected={tab === 'funnel'}
                    tabIndex={tab === 'funnel' ? 0 : -1}
                    aria-controls="panel-funnel"
                    aria-describedby={m.entryHints ? 'funnel-hint' : undefined}
                    className={`tab${tab === 'funnel' ? ' tab-current' : ''}`}
                    onClick={() => setTab('funnel')}
                  >
                    <span>{m.tabFunnel}</span>
                    {m.entryHints && (
                      <small id="funnel-hint" aria-hidden="true">
                        {m.entryHints.funnel}
                      </small>
                    )}
                  </button>
                </div>

                {/* Both panels stay mounted. The form holds what the user has typed and the funnel
                  holds what they have pasted, and switching tabs to look at the other one is not
                  a reason to lose either. */}
                <div
                  role={editing !== null ? 'region' : 'tabpanel'}
                  id="panel-form"
                  aria-labelledby={editing !== null ? 'editing-heading' : 'tab-form'}
                  hidden={tab !== 'form'}
                >
                  {editing !== null && (
                    <div className="editing-header">
                      <h2 id="editing-heading">
                        {fillSite(editMessages.heading, {
                          title: savedDraft?.title || m.listUntitled,
                        })}
                      </h2>
                      {hasUnsavedChanges && <p role="status">{editMessages.unsaved}</p>}
                    </div>
                  )}
                  {restored && (
                    <p className="recovery-notice" role="status">
                      {editing !== null && hasUnsavedChanges
                        ? editMessages.restoredEdit
                        : editMessages.restoredDraft}
                    </p>
                  )}
                  <EventCreator
                    key={loadedKey}
                    initialSession={restoredSession.current}
                    onSessionChange={setEditorSession}
                    showPreview={false}
                    simpleExport
                    downloadHelp={m.quickStart.downloadHelp}
                    collectionMode={editing !== null}
                    onCancelEditing={editing !== null ? cancelEditing : undefined}
                    cancelEditingLabel={editMessages.cancel}
                    onAdd={addFromForm}
                    onDraftChange={observeDraft}
                    addLabel={editing === null ? m.addToList : m.saveChanges}
                    locale={language}
                    messages={strings.creator}
                    {...(loaded === undefined ? {} : { initialDraft: loaded })}
                  />
                </div>

                <div
                  role="tabpanel"
                  id="panel-funnel"
                  aria-labelledby="tab-funnel"
                  hidden={tab !== 'funnel'}
                >
                  <Funnel
                    typed={text}
                    setTyped={setText}
                    m={m}
                    language={language}
                    onBatch={(batch) => {
                      collect(batch);
                      setReviewKey((key) => key + 1);
                    }}
                  />
                </div>
              </div>
              <div className="card maker-calendar" id="calendar-events" tabIndex={-1}>
                <p className="edit-confirmation" role="status">
                  {confirmation}
                </p>
                {collected !== null && collected.forms.length > 0 && (
                  <EventList
                    batch={collected}
                    editing={editing}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onSave={() => {
                      if (previewDraft) addFromForm(previewDraft);
                    }}
                    m={m}
                    creatorMessages={strings.creator}
                    locale={language}
                    onNew={() => openInForm(undefined, null)}
                    onEdit={(form) => openInForm(form.draft, form.id)}
                    onRemove={remove}
                    onDownload={download}
                  />
                )}
                {!collected?.forms.length && !previewDraft?.title.trim() && (
                  <div className="calendar-empty">
                    <h2>{m.preview.region}</h2>
                    <p className="muted">{m.preview.empty}</p>
                  </div>
                )}
                {Boolean(collected?.forms.length) && (
                  <p className="download-help">{m.quickStart.downloadHelp}</p>
                )}
                {(Boolean(previewDraft?.title.trim()) || Boolean(collected?.forms.length)) && (
                  <details className="calendar-preview">
                    <summary>{m.preview.region}</summary>
                    <WeekPreview
                      messages={m.preview}
                      locale={language}
                      events={[
                        ...(collected?.forms ?? []).map((form) => ({
                          id: form.id,
                          draft: form.id === editing && previewDraft ? previewDraft : form.draft,
                          pending: form.id === editing && hasUnsavedChanges,
                        })),
                        ...(editing === null && previewDraft?.title.trim()
                          ? [{ id: 'current-draft', draft: previewDraft, pending: true }]
                          : []),
                      ]}
                      focusDate={
                        tab === 'form' && previewDraft?.title.trim()
                          ? previewDraft.start.kind === 'date'
                            ? previewDraft.start.date
                            : previewDraft.start.dateTime.slice(0, 10)
                          : collected?.forms[0]?.draft.start.kind === 'date'
                            ? collected.forms[0].draft.start.date
                            : collected?.forms[0]?.draft.start.dateTime.slice(0, 10)
                      }
                      onSelect={(id) => {
                        if (id === 'current-draft' || id === editing) {
                          setTab('form');
                        } else {
                          const form = collected?.forms.find((item) => item.id === id);
                          if (form) openInForm(form.draft, form.id);
                        }
                        document
                          .getElementById('tab-form')
                          ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                      }}
                    />
                  </details>
                )}
              </div>
              <p className="sr-only" role="status">
                {collected === null
                  ? ''
                  : fillSite(m.workflow.headline, { count: collected.forms.length })}
              </p>
            </>
          ) : (
            <div className="card">
              <CreatorPlaceholder m={m} />
            </div>
          )}
        </section>

        <section className="answer" aria-labelledby="answer-question">
          <h2 id="answer-question">{page.question}</h2>
          <p>{page.answer}</p>
        </section>

        <section className="prose">
          {page.body()}

          {/* Rendered from the same data as the FAQPage JSON-LD, so the two can never disagree —
              structured data claiming an answer the page doesn't show is a manual-action risk. */}
          <h2>{m.questionsHeading}</h2>
          <dl className="faq">
            {page.faq.map((item, index) => (
              <div key={item.question} id={`faq-${index + 1}`}>
                <dt>{item.question}</dt>
                <dd>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>

      <footer className="site-footer">
        {/* Every page links to every other. It is a small site: nobody — reader or crawler —
            should ever be more than one click from any of it. */}
        <nav aria-label={m.pagesLabel}>
          <ul>
            {navigationPages
              .filter((other) => other.slug !== page.slug)
              .map((other) => (
                <li key={other.slug}>
                  <a href={hrefFor(other)}>{other.navLabel}</a>
                </li>
              ))}
          </ul>
        </nav>
        <p>{m.privacyNotice}</p>
      </footer>
    </div>
  );
}
